import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard } from 'lucide-react';
import LanguageSwitch from '../components/LanguageSwitch';
import Footer from '../components/Footer';
import BrandLogo from '../components/BrandLogo';

const SECTION_IDS = ['features', 'pricing', 'testimonials', 'faq'] as const;
const SECTION_KEYS: Record<(typeof SECTION_IDS)[number], string> = {
  features: 'home.navFeatures',
  pricing: 'home.navPricing',
  testimonials: 'home.navTestimonials',
  faq: 'home.navFaq',
};
const NAV_CTA_KEY = 'home.navCta';
const NAV_PANEL_KEY = 'home.navPanel';
const NAV_HOME_KEY = 'home.navHome';

export default function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <header className="sticky top-0 z-50 px-3 sm:px-4 pt-3">
        <div className="max-w-6xl mx-auto glass-card rounded-2xl !border-gray-200/80 dark:!border-gray-700/80 shadow-sm shadow-black/5">
          <div className="px-3 sm:px-5 flex items-center justify-between h-14">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-900 dark:text-white font-semibold hover:opacity-90"
            >
              <BrandLogo className="h-8 w-8 rounded-lg shadow-sm" />
              {t('common.appName')}
            </Link>
            <nav className="flex items-center gap-1 sm:gap-2" aria-label={t('appLayout.primaryNav')}>
              {!isHome && (
                <Link
                  to="/"
                  className="hidden sm:inline-flex text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-2 py-1.5 rounded-md hover:bg-gray-100/70 dark:hover:bg-gray-700/60 transition-colors"
                >
                  {t(NAV_HOME_KEY)}
                </Link>
              )}
              {SECTION_IDS.map((id) =>
                isHome ? (
                  <a
                    key={id}
                    href={`#${id}`}
                    className="hidden sm:inline-flex text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-2 py-1.5 rounded-md hover:bg-gray-100/70 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    {t(SECTION_KEYS[id])}
                  </a>
                ) : (
                  <Link
                    key={id}
                    to={{ pathname: '/', hash: id }}
                    className="hidden sm:inline-flex text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-2 py-1.5 rounded-md hover:bg-gray-100/70 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    {t(SECTION_KEYS[id])}
                  </Link>
                )
              )}
              <Link
                to="/register"
                className="hidden sm:inline-flex text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 px-2 py-1.5 rounded-md hover:bg-primary-50/70 dark:hover:bg-primary-900/20 transition-colors"
              >
                {t(NAV_CTA_KEY)}
              </Link>
              <LanguageSwitch variant="compact" />
              <Link
                to="/panel"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-purple-600 hover:shadow-lg hover:shadow-primary-500/25 px-3 py-2 rounded-lg shadow-sm transition-shadow"
              >
                <LayoutDashboard className="h-4 w-4" />
                {t(NAV_PANEL_KEY)}
              </Link>
            </nav>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
